import { combineReducers } from 'redux';
import { call, put, takeLatest, select, debounce } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { JournalingCategory, JournalingEvent, JournalingEventListResponse } from 'generated/bffJournaling/models';
import { ExternalUsersResponse } from 'generated/bffUser/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import dayjs from 'helpers/dayjs';
import { downloadUrl } from 'helpers/downloadHandler';
import {
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createLoadingStateReducer,
  createReducer,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { CustomerAccountInfo } from 'models/context/CustomerAccountInfo';
import { CustomerInfo } from 'models/context/CustomerInfo';
import {
  JournalingFilterParams,
  GetEventListPayload,
  JournalingEntityType,
  GetEventDetailPayload,
} from 'models/Journaling';
import { Pagination } from 'models/Pagination';

import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { api as assetApi } from 'saga/context/asset/api';
import { api as customerApi } from 'saga/context/customer/api';
import { selectContextCustomerInfo } from 'saga/context/customer/ducks';
import { api as customerAccountApi } from 'saga/context/customerAccount/api';
import { selectContextCustomerAccountInfo } from 'saga/context/customerAccount/ducks';
import { api as externalApi } from 'saga/context/external/api';
import { selectContextInternalIsUserScreenFilterDisabled } from 'saga/context/internal/ducks';
import { api as tsApi } from 'saga/context/tariffSpace/api';
import { selectProperty } from 'saga/property/ducks';

/* STATE */

export interface JournalingState {
  data: JournalingEvent[];
  loadingStatus: LoadingStatus;
  dataDetail: JournalingEvent | null;
  totalCount: number;
  filter: JournalingFilterParams;
  filterCategories: JournalingCategory[];
  pagination: Pagination;
  loadPayload: GetEventListPayload | null;
  headerValue: string;
  externalUsersQuickSearch: ExternalUsersResponse;
}

/* ACTION TYPES */
export enum JournalingActionTypes {
  SetFilter = '@@Journaling/SET_FILTER',
  SetLoading = '@@Journaling/SET_LOADING',
  SetFilterDebounced = '@@Journaling/SET_FILTER_DEBOUNCED',
  SetPage = '@@Journaling/SET_PAGE',
  GetEventList = '@@Journaling/GET_EVENT_LIST',
  GetCategoryFilters = '@@Journaling/GET_CATEGORY_FILTERS',
  GetEventDetail = '@@Journaling/GET_EVENT_DETAIL',
  DownloadEventDocument = '@@Journaling/DOWNLOAD_EVENT_DOCUMENT',
  StoreLoadPayload = '@@Journaling/SET_LOAD_PAYLOAD',
  GetEventListHeader = '@@Journaling/GET_EVENT_LIST_HEADER',
  GetExternalUsersQuickSearch = '@@Journaling/GET_EXTERNAL_USERS_QUICK_SEARCH',
  ClearExternalUsersQuickSearch = '@@Journaling/CLEAR_EXTERNAL_USERS_QUICK_SEARCH',
}

/* ACTIONS */
export const setFilterAction = createActionCreator(JournalingActionTypes.SetFilter);
export const setLoadingAction = createActionCreator(JournalingActionTypes.SetLoading);
export const setFilterDebouncedAction = createActionCreator(JournalingActionTypes.SetFilterDebounced);
export const setPageAction = createActionCreator(JournalingActionTypes.SetPage);
export const getEventListActions = createApiActionCreators(JournalingActionTypes.GetEventList);
export const getCategoryFiltersActions = createApiActionCreators(JournalingActionTypes.GetCategoryFilters);
export const getEventDetailActions = createApiActionCreators(JournalingActionTypes.GetEventDetail);
export const downloadEventDocumentAction = createActionCreator(JournalingActionTypes.DownloadEventDocument);
export const storeLoadPayloadAction = createActionCreator(JournalingActionTypes.StoreLoadPayload);
export const getEventListHeaderActions = createApiActionCreators(JournalingActionTypes.GetEventListHeader);
export const getExternalUsersQuickSearchActions = createApiActionCreators(
  JournalingActionTypes.GetExternalUsersQuickSearch
);
export const clearExternalUsersQuickSearchAction = createActionCreator(
  JournalingActionTypes.ClearExternalUsersQuickSearch
);

/* REDUCERS */
const initialState: JournalingState = {
  data: [],
  loadingStatus: LoadingStatus.loading,
  dataDetail: null,
  totalCount: 0,
  filterCategories: [],
  filter: {
    originatorType: 'all',
    eventDateTo: dayjs().format('YYYY-MM-DD'),
    eventDateFrom: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
  },
  pagination: {
    page: 1,
    pageSize: 0,
    forceCheck: false,
    initPages: 1,
  },
  loadPayload: null,
  headerValue: '',
  externalUsersQuickSearch: {
    externalUserList: [],
  },
};

const dataDetail = createReducer(initialState.dataDetail, {
  [JournalingActionTypes.GetEventDetail]: {
    [RequestActionTypes.REQUEST]: (state: JournalingEvent, payload: GetEventDetailPayload) => {
      if (Number(payload.eventID) !== state?.eventId) {
        return null;
      }

      return state;
    },
    [RequestActionTypes.SUCCESS]: (state: JournalingEvent, payload: JournalingEvent) => payload,
    [RequestActionTypes.FAILURE]: () => null,
  },
});

const data = createReducer(initialState.data, {
  [JournalingActionTypes.GetEventList]: {
    [RequestActionTypes.SUCCESS]: (state: JournalingEvent[], payload: JournalingEventListResponse & Pagination) => {
      if (payload.page > 1) {
        return [...state, ...(payload.eventList || [])];
      }

      return payload.eventList;
    },
    [RequestActionTypes.FAILURE]: () => [],
  },
});

const loadingStatus = createLoadingStateReducer(
  initialState.loadingStatus,
  {
    [JournalingActionTypes.GetEventList]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
  },
  {
    [JournalingActionTypes.SetLoading]: (state: LoadingStatus, status: LoadingStatus) => status,
  }
);

const totalCount = createReducer(initialState.totalCount, {
  [JournalingActionTypes.GetEventList]: {
    [RequestActionTypes.SUCCESS]: (_state: number, payload: JournalingEventListResponse) => payload.itemsCount,
    [RequestActionTypes.FAILURE]: () => 0,
  },
});

const filterCategories = createReducer(initialState.filterCategories, {
  [JournalingActionTypes.GetCategoryFilters]: {
    [RequestActionTypes.SUCCESS]: (_state: JournalingCategory[], payload: JournalingCategory[]) => payload,
    [RequestActionTypes.FAILURE]: () => [],
  },
});

const filter = createReducer(initialState.filter, {
  [JournalingActionTypes.SetFilter]: (state: JournalingFilterParams, payload: Partial<JournalingFilterParams>) => ({
    ...state,
    ...payload,
  }),
  [JournalingActionTypes.SetFilterDebounced]: (
    state: JournalingFilterParams,
    payload: Partial<JournalingFilterParams>
  ) => ({
    ...state,
    ...payload,
  }),
});

const pagination = createReducer(initialState.pagination, {
  [JournalingActionTypes.SetPage]: (_state: Pagination, payload: Pagination) => payload,
  [JournalingActionTypes.SetFilter]: (state: Pagination) => ({
    ...state,
    page: initialState.pagination.page,
  }),
  [JournalingActionTypes.SetFilterDebounced]: (state: Pagination) => ({
    ...state,
    page: initialState.pagination.page,
  }),
  [JournalingActionTypes.GetEventList]: {
    [RequestActionTypes.SUCCESS]: (state: Pagination) => ({
      ...state,
      page: state.page < state.initPages ? state.initPages : state.page,
    }),
  },
});

const loadPayload = createReducer(initialState.loadPayload, {
  [JournalingActionTypes.StoreLoadPayload]: (
    _state: GetEventListPayload | null,
    payload: GetEventListPayload | null
  ) => {
    return {
      ...payload,
      isInitial: false,
    };
  },
});

const headerValue = createReducer(initialState.headerValue, {
  [JournalingActionTypes.GetEventListHeader]: {
    [RequestActionTypes.SUCCESS]: (_state: string, payload: string) => payload,
    [RequestActionTypes.FAILURE]: () => initialState.headerValue,
  },
});

const externalUsersQuickSearch = createReducer(initialState.externalUsersQuickSearch, {
  [JournalingActionTypes.ClearExternalUsersQuickSearch]: () => initialState.externalUsersQuickSearch,
  [JournalingActionTypes.GetExternalUsersQuickSearch]: {
    [RequestActionTypes.SUCCESS]: (_state: ExternalUsersResponse, payload: ExternalUsersResponse) => {
      return payload;
    },
    [RequestActionTypes.FAILURE]: () => initialState.externalUsersQuickSearch,
  },
});

export default combineReducers<JournalingState>({
  data,
  loadingStatus,
  dataDetail,
  totalCount,
  filterCategories,
  filter,
  pagination,
  loadPayload,
  headerValue,
  externalUsersQuickSearch,
});

/* SELECTORS*/
const selectJournaling = (state: AppState) => state.journaling;

export const selectJournalingLoading = (state: AppState): LoadingStatus => selectJournaling(state).loadingStatus;

export const selectJournalingFilter = (state: AppState): JournalingFilterParams => selectJournaling(state).filter;

export const selectJournalingPagination = (state: AppState): Pagination => selectJournaling(state).pagination;

export const selectJournalingData = (state: AppState): JournalingEvent[] => {
  const isInternal = selectContextInternalIsUserScreenFilterDisabled(state);

  return selectJournaling(state)
    .data.filter((item) => !item.docGuid || item.objectStoreId)
    .filter((item) => item.visibleExtUser === 'X' || isInternal);
};

export const selectJournalingTotalCount = (state: AppState): number => selectJournaling(state).totalCount;

export const selectJournalingFilterCategories = (state: AppState): string[] =>
  selectJournaling(state)
    .filterCategories.filter((category) => category.categoryName)
    .map((category) => category.categoryName) as string[];

export const selectJournalingFilterSubCategories = (state: AppState): string[] => {
  const categoryName = selectJournalingFilter(state).categoryName;

  return (selectJournaling(state)
    .filterCategories.find((category) => category.categoryName === categoryName)
    ?.eventSubtypeList?.map((subtype) => subtype.eventSubtype) || []) as string[];
};

export const selectJournalingDetailData = (state: AppState): JournalingEvent | null =>
  selectJournaling(state).dataDetail;

export const selectStoredPayload = (state: AppState): GetEventListPayload | null => selectJournaling(state).loadPayload;

export const selectJournalingListHeaderValue = (state: AppState): string => selectJournaling(state).headerValue;

export const selectExternalUsersList = (state: AppState): ExternalUsersResponse =>
  selectJournaling(state).externalUsersQuickSearch;

/* SAGAS */
function* getEventDefaultPagination() {
  const pageSize = Number(selectProperty('gui.journalingHistory.pageSize'));
  const initPages = Number(selectProperty('gui.journalingHistory.loadPagesCount.Init'));

  const pagination: Pagination = yield select(selectJournalingPagination);

  yield put(
    setPageAction({
      ...pagination,
      initPages,
      page: 1,
      pageSize: pageSize || 50,
    })
  );
}

function* getEventTitleEntity({ payload }: any) {
  const urlParams = new URLSearchParams(window.location.search);
  const tsRefNo = urlParams.get('tsRefNo');
  const { entityType, entityRefNo } = payload;

  if (entityType === JournalingEntityType.Customer) {
    const request: ApiClientRequest = {
      apiCall: call(customerApi.getCustomer, yield getApiUrl(environment.urlConfig.customer), entityRefNo),
    };
    const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

    if (resp.ok) {
      if (resp.data) {
        yield put(getEventListHeaderActions.success(resp.data.displayName));
      }
    }
  } else if (entityType === JournalingEntityType.CustomerAccount) {
    const contextCustomerAccount: CustomerAccountInfo = yield select(selectContextCustomerAccountInfo);

    if (contextCustomerAccount?.caRefNo) {
      const request: ApiClientRequest = {
        apiCall: call(
          customerAccountApi.getCustomerAccount,
          yield getApiUrl(environment.urlConfig.customerAccount),
          contextCustomerAccount.caRefNo
        ),
      };
      const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

      if (resp.ok) {
        yield put(getEventListHeaderActions.success(`${resp.data.customName} ${resp.data.caRefNo}`));
      }
    }
  } else if (entityType === JournalingEntityType.ContactPerson) {
    const request: ApiClientRequest = {
      apiCall: call(externalApi.getExternalUser, yield getApiUrl(environment.urlConfig.user), entityRefNo, false),
    };
    const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

    if (resp.ok) {
      yield put(getEventListHeaderActions.success(resp.data.displayName));
    }
  } else if (entityType === JournalingEntityType.Asset) {
    if (tsRefNo) {
      const request: ApiClientRequest = {
        apiCall: call(tsApi.getTariffSpaceDetail, yield getApiUrl(environment.urlConfig.tariffSpace), tsRefNo),
      };
      const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

      if (resp.ok) {
        yield put(
          getEventListHeaderActions.success(
            resp.data?.tariffCustomName || resp.data?.tariffProductCodeLocale?.displayName
          )
        );
      } else {
        const request: ApiClientRequest = {
          apiCall: call(assetApi.getAssetDetail, yield getApiUrl(environment.urlConfig.asset), entityRefNo),
        };
        const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

        if (resp.ok) {
          yield put(
            getEventListHeaderActions.success(resp.data?.customName || resp.data?.productCodeLocale?.displayName)
          );
        }
      }
    } else {
      const request: ApiClientRequest = {
        apiCall: call(assetApi.getAssetDetail, yield getApiUrl(environment.urlConfig.asset), entityRefNo),
      };
      const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

      if (resp.ok) {
        yield put(
          getEventListHeaderActions.success(resp.data?.customName || resp.data?.productCodeLocale?.displayName)
        );
      }
    }
  } else {
    yield put(getEventListHeaderActions.success(''));
  }
}

function* getEventList(action: any) {
  if (action?.meta?.skipSaga) {
    return;
  }

  let { payload } = action;

  yield put(setLoadingAction(LoadingStatus.loading));

  const isInitial = payload.isInitial;
  const filter: JournalingFilterParams = yield select(selectJournalingFilter);
  const pagination: Pagination = yield select(selectJournalingPagination);

  if (payload.entityType) {
    yield put(storeLoadPayloadAction(payload));
  } else {
    payload = (yield select(selectStoredPayload)) as GetEventListPayload;
  }

  if (!pagination.pageSize || isInitial) {
    yield call(getEventDefaultPagination);

    return;
  }

  const paginationParam = {
    ...pagination,
    page: pagination.page < pagination.initPages ? 1 : pagination.page,
    pageSize: pagination.page < pagination.initPages ? pagination.pageSize * pagination.initPages : pagination.pageSize,
  };
  const dateFromValue: Date | null = filter.eventDateFrom ? new Date(filter.eventDateFrom) : null;
  const dateToValue: Date | null = filter.eventDateTo ? new Date(filter.eventDateTo) : null;
  const isRangeValidOrUnset: boolean = !dateFromValue || !dateToValue || dateFromValue <= dateToValue;

  if (isRangeValidOrUnset) {
    const request: ApiClientRequest = {
      apiCall: call(
        api.getEventList,
        yield getApiUrl(environment.urlConfig.journaling),
        payload?.entityType,
        payload?.entityRefNo,
        filter,
        paginationParam
      ),
    };
    const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

    if (resp.ok) {
      if (resp.data) {
        yield put(getEventListActions.success(resp.data));
      }
    } else {
      yield put(getEventListActions.failure());
    }
  }
}

function* getCategoryFilters({ payload }: any) {
  const request: ApiClientRequest = {
    apiCall: call(
      api.getCategoryFilters,
      yield getApiUrl(environment.urlConfig.journaling),
      payload.entityType,
      payload.entityRefNo
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getCategoryFiltersActions.success(resp.data.categoryNameList));
  } else {
    yield put(getCategoryFiltersActions.failure());
  }
}

function* getEventDetail({ payload }: any) {
  const request: ApiClientRequest = {
    apiCall: call(
      api.getEventDetail,
      yield getApiUrl(environment.urlConfig.journaling),
      payload.entityType,
      payload.entityRefNo,
      payload.eventID
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok && resp.data?.eventList?.length > 0) {
    yield put(getEventDetailActions.success(resp.data.eventList[0]));
  } else {
    yield put(getEventDetailActions.failure());
  }
}

function* downloadEventDocument({ payload }: any) {
  const request: ApiClientRequest = {
    apiCall: call(
      api.getEventDetail,
      yield getApiUrl(environment.urlConfig.journaling),
      payload.entityType,
      payload.entityRefNo,
      payload.eventID
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    if (resp.data?.eventList?.length > 0 && resp.data.eventList[0].docDownloadUrl) {
      downloadUrl(resp.data.eventList[0]?.docDownloadUrl);
    }
  } else {
    yield put(getEventDetailActions.failure());
  }
}

function* getExternalUsersQuickSearch({ payload: searchString }: any) {
  if (!searchString) {
    return;
  }

  const customer: CustomerInfo = yield select(selectContextCustomerInfo);
  const loadPageCount: string = selectProperty('gui.journalingHistory.loadPageCount.contactPersonSearch');
  const loadPageSize: string = selectProperty('gui.journalingHistory.loadPageSize.contactPersonSearch');
  const minCharsToSearch: string = selectProperty('gui.journalingHistory.minCharsToSearch');

  if (searchString.length >= minCharsToSearch) {
    const request: ApiClientRequest = {
      apiCall: call(
        externalApi.externalUsersQuickSearch,
        yield getApiUrl(environment.urlConfig.externalUserAccesses),
        customer?.cuRefNo || '',
        '2-33-1',
        searchString,
        loadPageCount,
        loadPageSize
      ),
    };
    const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

    if (resp.ok) {
      yield put(getExternalUsersQuickSearchActions.success(resp.data));
    } else {
      yield put(getExternalUsersQuickSearchActions.failure());
    }
  }
}

/* EXPORT */
export function* journalingSaga() {
  yield takeLatest(
    [
      createActionType(JournalingActionTypes.GetEventList, RequestActionTypes.REQUEST),
      JournalingActionTypes.SetFilter,
      JournalingActionTypes.SetPage,
    ],
    getEventList
  );

  yield debounce(1000, JournalingActionTypes.SetFilterDebounced, getEventList);

  yield takeLatest(
    createActionType(JournalingActionTypes.GetCategoryFilters, RequestActionTypes.REQUEST),
    getCategoryFilters
  );

  yield takeLatest(createActionType(JournalingActionTypes.GetEventDetail, RequestActionTypes.REQUEST), getEventDetail);

  yield takeLatest(JournalingActionTypes.DownloadEventDocument, downloadEventDocument);

  yield takeLatest(
    createActionType(JournalingActionTypes.GetEventListHeader, RequestActionTypes.REQUEST),
    getEventTitleEntity
  );

  yield takeLatest(
    [
      createActionType(JournalingActionTypes.GetExternalUsersQuickSearch, RequestActionTypes.REQUEST),
      JournalingActionTypes.ClearExternalUsersQuickSearch,
    ],
    getExternalUsersQuickSearch
  );
}
